import { ExternalLink, LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Callout, Spinner } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { CopyButton } from "@/components/ui/misc";
import { ApiError } from "@/lib/api";
import { ROLE_LABELS, type AppUser, type SignInLinkMade } from "@/lib/types";
import { formatDateTime } from "@/lib/utils";

/**
 * The link "Sign in as" makes (ADR-040 in `platform`): opened in another browser, it signs that
 * browser in as the person, once. The Users page asks for it when the item is chosen, so this shows
 * the request as it goes; making it in an effect here would make two under StrictMode.
 */
export function SignInLinkDialog({
  user, made, error, pending, open, onOpenChange, onRetry,
}: {
  /** Kept while the dialog animates shut, so it does not empty as it closes. */
  user: AppUser | undefined;
  made: SignInLinkMade | undefined;
  error: unknown;
  pending: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRetry: () => void;
}) {
  // A link made for somebody else, the last time the dialog was open, is not this one.
  const link = made && user && made.user_id === user.id ? made : undefined;
  const name = user ? user.display_name || user.email : "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md">
        {user && (
          <>
            <DialogHeader>
              <DialogTitle>Sign in as {name}</DialogTitle>
              <DialogDescription>
                {user.display_name ? `${user.email} · ` : ""}
                {ROLE_LABELS[user.role]}. Open it in a new tab here, or copy the link into another
                browser. Either way you stay signed in as yourself in this one, and the console
                shows a bar with Exit while you are them.
              </DialogDescription>
            </DialogHeader>
            <DialogBody className="space-y-4">
              {link ? (
                <>
                  <Field
                    label="Sign-in link"
                    htmlFor="sign-in-link"
                    hint={`It works once, until ${formatDateTime(link.expires_at)}. Opening it in a new tab uses it. For another, choose Sign in as again.`}
                  >
                    <Input
                      id="sign-in-link"
                      readOnly
                      value={link.url}
                      className="font-mono text-xs"
                      onFocus={(e) => e.currentTarget.select()}
                      trailing={<CopyButton value={link.url} label="Copy the link" />}
                    />
                  </Field>
                  <p className="text-xs text-text-muted">
                    Their phone stays off while you are signed in as them.
                  </p>
                </>
              ) : pending || !error ? (
                <p className="flex items-center gap-2 text-xs text-text-muted">
                  <Spinner className="size-3.5" />
                  Making a link…
                </p>
              ) : (
                <Callout>
                  {error instanceof ApiError ? error.message : "Could not reach the server."}
                </Callout>
              )}
            </DialogBody>
            <DialogFooter>
              {!link && !pending && Boolean(error) && (
                <Button variant="outline" onClick={onRetry}>
                  <LogIn />
                  Try again
                </Button>
              )}
              {link ? (
                <>
                  <Button variant="outline" onClick={() => onOpenChange(false)}>
                    Done
                  </Button>
                  <Button
                    variant="default"
                    onClick={() => {
                      // In the click itself, so no popup blocker stands in the way. `noopener`: the
                      // new tab shares nothing of this one, its session storage included.
                      window.open(link.url, "_blank", "noopener");
                      onOpenChange(false);
                    }}
                  >
                    <ExternalLink />
                    Open in a new tab
                  </Button>
                </>
              ) : (
                <Button variant="default" onClick={() => onOpenChange(false)}>
                  Done
                </Button>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
